/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { request } from '../../../config/helpers/axios-instance';

// "Add Extra Items" — meds that must be packed separately from the pouches
// (e.g. controlled drugs, cold-chain, fragile strips). Surfaced to the logistics team as the
// "Meds to Pack Separately" column on the Orders tab of the logistics console
// (`.scratch/logistics-dashboard/IMPLEMENTATION.md` §10).
//
// Stored as `userOrders.additionalDetails.extraItems`, a free-text string.
//
// Saves through PUT /userOrder/admin/extraItems/:id rather than the general order PUT:
// additionalDetails is one shared JSONB column that also carries `billNumber` (written by
// the eVital sync), and the general endpoint replaces it wholesale. The dedicated route
// read-modify-writes so nothing else in there is lost.
const ExtraItemsModal = ({ orderId, initialValue = '', onClose, onSaved }) => {
    const [value, setValue] = useState(initialValue || '');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const response = await request({
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                url: `userOrder/admin/extraItems/${orderId}`,
                data: { extraItems: value },
            });

            if (response.data?.success) {
                // Clearing the box is a real action, not a no-op — say which one happened.
                toast.success(value.trim() ? 'Extra items saved' : 'Extra items cleared');
                if (onSaved) onSaved(response.data?.data?.extraItems ?? '');
                onClose();
                return;
            }
            toast.error(response.data?.err || "Couldn't save extra items");
        } catch (error) {
            toast.error("Couldn't save extra items");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex flex-col gap-[0.9rem] min-w-[420px]">
            <div className="flex justify-between items-center">
                <span className="text-[1.5rem] font-bold">Add Extra Items</span>
                <span className="text-[1.2rem]">
                    Order ID: <span className="text-[1.2rem] font-semibold">{`#${orderId}`}</span>
                </span>
            </div>
            <hr />

            <label className="text-[1.1rem] text-gray-600" htmlFor="extra-items-input">
                Medicines that must be packed separately from the pouches. Visible to the
                logistics team on the Orders queue.
            </label>

            <textarea
                id="extra-items-input"
                className="w-full min-h-[140px] rounded-lg border border-gray-300 p-3 text-[1.2rem] focus:outline-none focus:ring-2 focus:ring-[#00A48B]"
                placeholder="e.g. Insulin — cold chain, keep in separate pouch"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                autoFocus
            />

            <div className="flex justify-end gap-3">
                <button
                    type="button"
                    onClick={onClose}
                    disabled={saving}
                    className="px-5 py-2 rounded-xl border border-gray-300 text-[1.2rem] font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                    Close
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving}
                    className="px-5 py-2 rounded-xl bg-[#00A48B] text-[1.2rem] font-semibold text-white-50 hover:bg-[#008f79] disabled:opacity-50"
                >
                    {saving ? 'Saving...' : 'Save'}
                </button>
            </div>
        </div>
    );
};

export default ExtraItemsModal;
